import { useMemo } from 'react';
import { useEntityManagement, EntityCrudService } from '../../../shared/hooks/useEntityManagement';
import { useCatalogService, Portfolio } from '../services/catalogService';

export interface PortfolioFormData {
  name: string;
  display_name: string;
  description: string;
}

const EMPTY_FORM: PortfolioFormData = {
  name: '',
  display_name: '',
  description: '',
};

export function usePortfolioManagement(isOpen: boolean) {
  const catalogService = useCatalogService();

  const crudService = useMemo((): EntityCrudService<Portfolio, Record<string, unknown>, Record<string, unknown>> => ({
    getAll: async () => {
      const result = await catalogService.listPortfolios();
      return result.portfolios;
    },
    create: (dto) => catalogService.createPortfolio(dto as Parameters<typeof catalogService.createPortfolio>[0]),
    update: (id, dto) => catalogService.updatePortfolio(id, dto),
    delete: async (id) => {
      await catalogService.deletePortfolio(id);
    },
  }), [catalogService]);

  const entityManagement = useEntityManagement<Portfolio, PortfolioFormData>({
    service: crudService,
    queryKey: ['service-administration', 'portfolios'],
    isOpen,
    emptyForm: EMPTY_FORM,
    getEntityId: (portfolio) => portfolio.id,
    entityToForm: (portfolio) => ({
      name: portfolio.name,
      display_name: portfolio.display_name,
      description: portfolio.description,
    }),
    formToCreateDto: (form) => ({
      name: form.name,
      display_name: form.display_name || undefined,
      description: form.description || undefined,
    }),
    formToUpdateDto: (form) => ({
      name: form.name,
      display_name: form.display_name,
      description: form.description,
    }),
    validateForm: (form) => ({
      name: !form.name || form.name.trim().length < 2 ? 'Name must be at least 2 characters' : null,
    }),
    hasFormChanges: (form, portfolio, isNew) => {
      if (isNew) return true;
      if (!portfolio) return false;
      return (
        form.name !== portfolio.name ||
        form.display_name !== portfolio.display_name ||
        form.description !== portfolio.description
      );
    },
  });

  return { entityManagement };
}
